import React, { useMemo } from "react";
import { WABaseScreenParams, WAStack, WAStackParamList } from "./WANavigation";
import { WACategoryIntegrations } from "./Categories/WACategoryIntegrations/WACategoryIntegrations";
import { WACategoryChecklists } from "./Categories/WACategoryChecklists/WACategoryChecklists";
import { WACategoryWeather } from "./Categories/WACategoryWeather";
import { WACategoryTeamSchedule } from "./Categories/WACategoryTeamSchedule/WACategoryTeamSchedule";
import { WACategoryNote } from "./Categories/WACategoryNote/WACategoryNote";
import { WACategoryKPI } from "./Categories/WACategoryKPI/WACategoryKPI";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RouteProp } from "@react-navigation/native";
import { WidgetAddMaster } from "./WidgetAddMaster";
import { useWindowDimensions } from "react-native";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { WidgetSizeT } from "../localTypes/WidgetSizeT";
import { EmptyPositionT } from "../EmptyPositions/EmptyPosType";

import { useTheme } from "arkh";
import { useIntl } from "react-intl";

import { WACategoryFile } from "./Categories/WACategoryFile/WACategoryFile";
import { WACategoryOrg } from "./Categories/WACategoryOrg";
import { useWACategoryLayout } from "./utils/useWACategoryLayout";
import { WACategoryTodoCategories } from "./Categories/WACategoryTodoCategories/WACategoryTodoCategories";
import { WACategoryDefaultTodoCategory } from "./Categories/WACategoryDefaultTodoCategory/WACategoryDefaultTodoCategory";
import { useNavigationHeaderOptions } from "../../../../common/navigationHeaderOptions";
import { WACategoryCalendar } from "./Categories/WACategoryCalendar/WACategoryCalendar";
import { WACategoryEmployeeRoles } from "./Categories/WACategoryEmployeeRoles";
import { WACategorySignals } from "./Categories/WACategorySignals";

export function WACompact(props: {
  maxSize: WidgetSizeT;
  storecastId: string;
  position: EmptyPositionT;
  defaultTeams: string[];
  onClose(): void;
}) {
  const theme = useTheme();
  const intlTokens = useIntlTokens();
  const intl = useIntl();
  const width = useWindowDimensions().width;
  const paddingHorizontal = useWACategoryLayout().paddingHorizontal;
  const unitSize = useMemo(() => {
    return width / 2 - paddingHorizontal;
  }, [paddingHorizontal, width]);
  const getNavigationHeaderOptions = useNavigationHeaderOptions();
  const initialParams = useMemo(
    () => ({
      storecastId: props.storecastId,
      unitSize,
      maxSize: props.maxSize,
      defaultTeams: props.defaultTeams,
      position: props.position,
    }),
    [
      props.defaultTeams,
      props.maxSize,
      props.position,
      props.storecastId,
      unitSize,
    ]
  );

  return (
    <WAStack.Navigator
      screenOptions={{
        headerStyle: {
          backgroundColor: theme.background,
        },
        headerBackTitle: intlTokens.Back,
        headerShadowVisible: true,
      }}
    >
      <WAStack.Screen
        name="WidgetAddMaster"
        component={WidgetAddMasterScreen}
        options={getNavigationHeaderOptions({
          type: "CLOSE",
          title: intl.formatMessage({
            defaultMessage: "Add widget",
            description: "WACompactAddWidget title",
          }),
          onClose: props.onClose,
        })}
        initialParams={initialParams}
      />
      <WAStack.Screen
        name="WACategoryIntegrations"
        component={WACategoryIntegrations}
        initialParams={initialParams}
        options={({ navigation }) =>
          getNavigationHeaderOptions({
            type: "BACK",
            title: intlTokens.Integrations,
            onBack: () => navigation.goBack(),
          })
        }
      />
      <WAStack.Screen
        name="WACategoryFile"
        component={WACategoryFile}
        initialParams={initialParams}
        options={({ navigation }) =>
          getNavigationHeaderOptions({
            type: "BACK",
            title: intlTokens.Files,
            onBack: () => navigation.goBack(),
          })
        }
      />
      <WAStack.Screen
        component={WACategoryChecklists}
        name="WACategoryChecklists"
        initialParams={initialParams}
        options={({ navigation }) =>
          getNavigationHeaderOptions({
            type: "BACK",
            title: intlTokens.Checklists,
            onBack: () => navigation.goBack(),
          })
        }
      />

      <WAStack.Screen
        name="WACategoryWeather"
        component={WACategoryWeather}
        initialParams={initialParams}
        options={({ navigation }) =>
          getNavigationHeaderOptions({
            type: "BACK",
            title: intlTokens.Weather,
            onBack: () => navigation.goBack(),
          })
        }
      />
      <WAStack.Screen
        name="WACategoryTeamSchedule"
        component={WACategoryTeamSchedule}
        initialParams={initialParams}
        options={({ navigation }) =>
          getNavigationHeaderOptions({
            type: "BACK",
            title: intlTokens.TeamSchedule,
            onBack: () => navigation.goBack(),
          })
        }
      />
      <WAStack.Screen
        name="WACategoryCalendar"
        component={WACategoryCalendar}
        initialParams={initialParams}
        options={({ navigation }) =>
          getNavigationHeaderOptions({
            type: "BACK",
            title: intlTokens.Calendar,
            onBack: () => navigation.goBack(),
          })
        }
      />
      <WAStack.Screen
        name="WACategoryNote"
        component={WACategoryNote}
        initialParams={initialParams}
        options={({ navigation }) =>
          getNavigationHeaderOptions({
            type: "BACK",
            title: intlTokens.Note,
            onBack: () => navigation.goBack(),
          })
        }
      />
      <WAStack.Screen
        name="WACategoryTodoCategories"
        component={WACategoryTodoCategories}
        initialParams={initialParams}
        options={{
          headerShown: false,
        }}
      />
      <WAStack.Screen
        name="WACategoryDefaultTodoCategory"
        component={WACategoryDefaultTodoCategory}
        initialParams={initialParams}
        options={({ navigation }) =>
          getNavigationHeaderOptions({
            type: "BACK",
            title: "",
            onBack: () => navigation.goBack(),
          })
        }
      />
      <WAStack.Screen
        name="WACategoryKPI"
        component={WACategoryKPI}
        initialParams={initialParams}
        options={({ navigation }) =>
          getNavigationHeaderOptions({
            type: "BACK",
            title: intlTokens.KPIs,
            onBack: () => navigation.goBack(),
          })
        }
      />
      <WAStack.Screen
        name="WACategoryOrgInfo"
        component={WACategoryOrg}
        initialParams={initialParams}
        options={({ navigation }) =>
          getNavigationHeaderOptions({
            type: "BACK",
            title: intlTokens.HQInfo,
            onBack: () => navigation.goBack(),
          })
        }
      />
      <WAStack.Screen
        name="WACategorySignals"
        component={WACategorySignals}
        initialParams={initialParams}
        options={({ navigation }) =>
          getNavigationHeaderOptions({
            type: "BACK",
            title: intlTokens.Signals,
            onBack: () => navigation.goBack(),
          })
        }
      />
      <WAStack.Screen
        name="WACategoryEmployeeRoles"
        component={WACategoryEmployeeRoles}
        initialParams={initialParams}
        options={({ navigation }) =>
          getNavigationHeaderOptions({
            type: "BACK",
            title: intlTokens.Lineup,
            onBack: () => navigation.goBack(),
          })
        }
      />
    </WAStack.Navigator>
  );
}

function WidgetAddMasterScreen(props: {
  navigation: NativeStackNavigationProp<WAStackParamList, "WidgetAddMaster">;
  route: RouteProp<WAStackParamList, "WidgetAddMaster">;
}) {
  return (
    <WidgetAddMaster
      chevron
      onPress={(input) => {
        if (input.key === "WACategoryDefaultTodoCategory") {
          props.navigation.navigateDeprecated(input.key, {
            storecastId: props.route.params.storecastId,
            unitSize: props.route.params.unitSize,
            maxSize: props.route.params.maxSize,
            defaultTeams: props.route.params.defaultTeams,
            position: props.route.params.position,
            categoryId: input.categoryId,
          } as WABaseScreenParams & { categoryId: string });
        } else {
          props.navigation.navigateDeprecated(input.key, {
            storecastId: props.route.params.storecastId,
            unitSize: props.route.params.unitSize,
            maxSize: props.route.params.maxSize,
            defaultTeams: props.route.params.defaultTeams,
            position: props.route.params.position,
          } as WABaseScreenParams);
        }
      }}
    />
  );
}
